import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../queryKeys'
import { auditAiRequest } from '../../util/api/audit-ai-api'
import useStore from '../../util/store/auth'
import checkToken from '../../util/auth/checkToken'
import useDashboardListFilters from '../../util/useDashboardListFilters'
import { getDashboardQueryContext, getSortedMeasureIdsForQueryKey } from './dashboardHookUtils'
import { getDashboardTimezone } from '../../util/dateUtils'

export const useDashboardGraph = () => {
  const { directory } = useStore()
  const { filters } = useDashboardListFilters()
  const timezone = getDashboardTimezone()
  const {
    scopeFilters: { clinicianEmail, patientMrn, measureIds, location, program },
    calendarDateRange: { fromDate, toDate },
  } = getDashboardQueryContext(filters, timezone)
  const sortedMeasureIdsForQueryKey = getSortedMeasureIdsForQueryKey(measureIds)
  const hasCalendarDateRange = !!fromDate && !!toDate
  const dateField = filters.dateMode
  const noVisitDate = filters.noDateOfService || undefined

  const queryKey = queryKeys.dashboardGraph(
    directory?._id ?? '',
    [
      fromDate,
      toDate,
      timezone,
      dateField,
      noVisitDate ? 'noVisitDate' : undefined,
      clinicianEmail,
      patientMrn,
      location,
      program,
      ...(sortedMeasureIdsForQueryKey ?? []),
    ].map(
      (el) => el ?? ''
    )
  )

  return useQuery<Awaited<ReturnType<typeof auditAiRequest.dashboard.graph>>>({
    queryKey,
    queryFn: async () => {
      await checkToken()
      if (!fromDate || !toDate) {
        throw new Error('Dashboard graph query requires a calendar date range')
      }

      return auditAiRequest.dashboard.graph({
        token: '',
        directoryId: directory?._id ?? '',
        fromDate,
        toDate,
        timezone,
        dateField,
        noVisitDate,
        clinicianEmail: clinicianEmail ?? undefined,
        patientMrn: patientMrn ?? undefined,
        location,
        program,
        measureIds,
      })
    },
    enabled: !!directory?._id && hasCalendarDateRange,
  })
}
